import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, FileText, AlertTriangle, CheckCircle, XCircle, Loader2, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/pages/Common';

const LogAnalysis = ({ language, deviceType, onComplete, onSkip }) => {
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const fileInputRef = useRef(null);

  const translations = {
    en: {
      title: 'Security Log Analysis',
      description: 'Upload your device diagnostic log to get an AI-powered security analysis.',
      uploadInstructions: 'Supported formats: .txt, .log, .json, .xml, .csv, .ips, .crash',
      dragDrop: 'Drag and drop a file here, or click to select',
      selectFile: 'Select File',
      analyzing: 'Analyzing your log with AI...',
      analysisComplete: 'Analysis Complete',
      riskLevel: 'Risk Level',
      suspiciousItems: 'Suspicious Items Found',
      recommendations: 'Recommendations',
      noIssuesFound: 'No significant security issues were found in your log.',
      skipAnalysis: 'Skip This Step',
      continueToResults: 'Continue to Results',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      critical: 'Critical',
      error: 'Error analyzing log file. Please try again.',
      showDetails: 'Show Details',
      hideDetails: 'Hide Details',
      fileSelected: 'File selected',
      analyzeNow: 'Analyze Now',
    },
    it: {
      title: 'Analisi Log di Sicurezza',
      description: 'Carica il file di log diagnostico del tuo dispositivo per ottenere un\'analisi di sicurezza basata su IA.',
      uploadInstructions: 'Formati supportati: .txt, .log, .json, .xml, .csv, .ips, .crash',
      dragDrop: 'Trascina qui un file, oppure clicca per selezionarlo',
      selectFile: 'Seleziona File',
      analyzing: 'Analisi del log con IA in corso...',
      analysisComplete: 'Analisi Completata',
      riskLevel: 'Livello di Rischio',
      suspiciousItems: 'Elementi Sospetti Trovati',
      recommendations: 'Raccomandazioni',
      noIssuesFound: 'Non sono stati trovati problemi di sicurezza significativi nel tuo log.',
      skipAnalysis: 'Salta Questo Passaggio',
      continueToResults: 'Continua ai Risultati',
      low: 'Basso',
      medium: 'Medio',
      high: 'Alto',
      critical: 'Critico',
      error: 'Errore durante l\'analisi del file di log. Riprova.',
      showDetails: 'Mostra Dettagli',
      hideDetails: 'Nascondi Dettagli',
      fileSelected: 'File selezionato',
      analyzeNow: 'Analizza Ora',
    }
  };

  const t = (key) => translations[language]?.[key] || translations.en[key];

  const getRiskColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'low': return 'text-green-400 border-green-400/50 bg-green-400/10';
      case 'medium': return 'text-yellow-400 border-yellow-400/50 bg-yellow-400/10';
      case 'high': return 'text-orange-400 border-orange-400/50 bg-orange-400/10';
      case 'critical': return 'text-red-400 border-red-400/50 bg-red-400/10';
      default: return 'text-gray-400 border-gray-400/50 bg-gray-400/10';
    }
  };

  const getRiskLabel = (level) => {
    switch (level?.toLowerCase()) {
      case 'low': return t('low');
      case 'medium': return t('medium');
      case 'high': return t('high');
      case 'critical': return t('critical');
      default: return level;
    }
  };

  const getRiskIcon = (level) => {
    switch (level?.toLowerCase()) {
      case 'low': return <CheckCircle className="w-6 h-6" />;
      case 'medium': return <AlertTriangle className="w-6 h-6" />;
      case 'high': return <AlertTriangle className="w-6 h-6" />;
      case 'critical': return <XCircle className="w-6 h-6" />;
      default: return <Shield className="w-6 h-6" />;
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setError(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const analyzeLog = async () => {
    if (!file) return;

    setAnalyzing(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('device_type', deviceType);

    try {
      const response = await axios.post(`${API}/analyze-logs`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setResult(response.data);
    } catch (err) {
      console.error('Log analysis error:', err);
      setError(err.response?.data?.detail || t('error'));
    } finally {
      setAnalyzing(false);
    }
  };

  // Show results view
  if (result) {
    return (
      <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="text-center mb-6">
          <Shield className="w-16 h-16 mx-auto mb-4 text-primary" />
          <h2 className="text-2xl md:text-3xl font-bold mb-2">{t('analysisComplete')}</h2>
        </div>

        {/* Risk Level Badge */}
        <div className={`flex items-center justify-center gap-3 p-4 rounded-sm border mb-6 ${getRiskColor(result.risk_level)}`}>
          {getRiskIcon(result.risk_level)}
          <div>
            <p className="text-sm uppercase tracking-widest opacity-80">{t('riskLevel')}</p>
            <p className="text-xl font-bold">{getRiskLabel(result.risk_level)}</p>
          </div>
        </div>

        {/* Analysis Summary */}
        <div className="glass-panel rounded-sm p-6 mb-6">
          <p className="text-muted-foreground leading-relaxed">{result.analysis}</p>
        </div>

        {/* Suspicious Items */}
        {result.suspicious_items && result.suspicious_items.length > 0 && (
          <div className="mb-6">
            <button 
              onClick={() => setShowDetails(!showDetails)}
              className="w-full flex items-center justify-between p-4 glass-panel rounded-sm hover:bg-white/5 transition-colors"
              data-testid="toggle-details-button"
            >
              <span className="font-mono uppercase tracking-widest text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                {t('suspiciousItems')} ({result.suspicious_items.length})
              </span>
              {showDetails ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            
            {showDetails && (
              <div className="mt-2 space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                {result.suspicious_items.map((item, idx) => (
                  <div key={idx} className="p-4 bg-zinc-900/50 border border-white/10 rounded-sm">
                    <div className="flex items-start gap-3">
                      <div className={`p-1 rounded ${
                        item.severity === 'high' ? 'bg-red-400/20 text-red-400' :
                        item.severity === 'medium' ? 'bg-yellow-400/20 text-yellow-400' :
                        'bg-gray-400/20 text-gray-400'
                      }`}>
                        <AlertTriangle className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm mb-1">{item.type?.replace(/_/g, ' ').toUpperCase()}</p>
                        <p className="text-muted-foreground text-sm">{item.description}</p>
                        {item.evidence && (
                          <code className="block mt-2 p-2 bg-black/30 rounded text-xs text-primary/80 overflow-x-auto">
                            {item.evidence}
                          </code>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recommendations */}
        {result.recommendations && result.recommendations.length > 0 && (
          <div className="glass-panel rounded-sm p-6 mb-6">
            <h3 className="font-mono uppercase tracking-widest text-sm mb-4 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              {t('recommendations')}
            </h3>
            <ul className="space-y-3">
              {result.recommendations.map((rec, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="text-primary mt-1">•</span>
                  <span className="text-muted-foreground">{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* No issues message */}
        {(!result.suspicious_items || result.suspicious_items.length === 0) && result.risk_level === 'low' && (
          <div className="glass-panel rounded-sm p-6 mb-6 border-green-400/20">
            <div className="flex items-center gap-3 text-green-400">
              <CheckCircle className="w-6 h-6" />
              <p>{t('noIssuesFound')}</p>
            </div>
          </div>
        )}

        {/* Continue Button */}
        <Button
          onClick={onComplete}
          data-testid="continue-after-analysis-button"
          className="w-full h-14 rounded-none border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono"
        >
          {t('continueToResults')}
        </Button>
      </div>
    );
  }

  // Show upload view
  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="text-center mb-6">
        <Shield className="w-16 h-16 mx-auto mb-4 text-primary" />
        <h2 className="text-2xl md:text-3xl font-bold mb-2">{t('title')}</h2>
        <p className="text-muted-foreground">{t('description')}</p>
      </div>

      {/* File Upload Area */}
      <div
        className={`glass-panel rounded-sm p-8 mb-6 border-2 border-dashed transition-colors cursor-pointer ${
          file ? 'border-primary/50' : 'border-white/20 hover:border-primary/30'
        }`}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        data-testid="file-drop-zone"
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".txt,.log,.json,.xml,.csv,.ips,.crash"
          className="hidden"
          data-testid="file-input"
        />
        
        <div className="text-center">
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText className="w-10 h-10 text-primary" />
              <div className="text-left">
                <p className="font-semibold">{file.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB - {t('fileSelected')}
                </p>
              </div>
            </div>
          ) : (
            <>
              <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg mb-2">{t('dragDrop')}</p>
              <p className="text-sm text-muted-foreground">{t('uploadInstructions')}</p>
            </>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-sm text-red-400">
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5" />
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Buttons */}
      <div className="space-y-3">
        {file && (
          <Button
            onClick={analyzeLog}
            disabled={analyzing}
            data-testid="analyze-button"
            className="w-full h-14 rounded-none border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono disabled:opacity-50"
          >
            {analyzing ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('analyzing')}
              </span>
            ) : (
              t('analyzeNow')
            )}
          </Button>
        )}
        
        <Button
          variant="outline"
          onClick={onSkip}
          disabled={analyzing}
          data-testid="skip-analysis-button"
          className="w-full border-white/20 hover:border-primary/50 rounded-none uppercase tracking-widest font-mono"
        >
          {t('skipAnalysis')}
        </Button>
      </div>
    </div>
  );
};

export default LogAnalysis;
